'use client';

import type { Extension } from '@tiptap/react';
import { Plus, Zap } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ChangeEvent } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { BaseLineSubmit3 } from '@/app/components/icons/BaseLineSubmit3';
import { SuggestionPluginKey } from '@/app/components/shared/MentionsTextarea/extensions/custom-suggestion';
import { useCustomMention } from '@/app/components/shared/MentionsTextarea/hooks/use-custom-mention';
import { Button } from '@/app/components/ui/button';
import { toast } from '@/app/components/ui/use-toast';
import { useClawAccess, useEditorWithExtensions } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type {
  ClawBillingMode,
  ClawTemplateTokenFile,
  ClawUploadAcceptKind,
  TextPromptWithInputItem,
} from '@/app/types';
import { cn, extractMentionsIdsFromEditor, reportEvent } from '@/app/utils';
import { type ImageValidationReason, validateImageFile } from '@/app/utils/client';
import { useClawImageToasts } from '../../../_hook/use-claw-image-toasts';
import { useClawInactiveHandler } from '../../../_hook/use-claw-inactive-handler';
import { useClawMentionSearch } from '../../../_hook/use-claw-mention-search';
import { useClawSkillSearch } from '../../../_hook/use-claw-skill-search';
import { useUploadClawFile } from '../../../_hook/use-upload';
import { useUploadWithRenameOnConflict } from '../../../_hook/use-upload-with-rename-on-conflict';
import {
  buildUploadAcceptAttr,
  CLAW_UPLOAD_ACCEPT,
  DEFAULT_MEDIA_ROOT,
  getMediaItemType,
  isAllowedUploadFile,
  isFileMatchingAccept,
  normalizePath,
  shouldDisplayMediaFile,
} from '../../../_utils';
import { deleteFileOrFolder, listFolder } from '../../../apis';
import type {
  ClawDraftFileType,
  ClawSendFile,
  QuickPrompt,
  SessionMode,
  UploadClawFileResult,
  YoloMessageOptions,
} from '../../../types';
import { getItemName, isFolder } from '../../File/utils';
import { BillingModeControls } from './BillingModeControls';
import { DraftAttachmentCard } from './DraftAttachmentCard';
import { DraftLocalPreviewDialog } from './DraftLocalPreviewDialog';
import { createClawTemplateTokenExtension, EditorArea } from './EditorArea';
import { createClawSkillSuggestionExtension } from './extensions/claw-skill-suggestion';
import { useClawInputAttachments, useClawInputCanSend, useClawInputDraft } from './hooks';
import {
  buildClawInputPayload,
  computeCanUpload,
  type DraftAttachment,
  extractFileNameFromUrl,
  type InsertAssetMentionArg,
  inferFileTypeFromPath,
  isPendingUploadAttachment,
  isSkillTemplateToken,
  MENTION_COLOR,
  makeMentionAttachment,
  makeQuickPendingAttachment,
  makeQuickPreviewAttachment as makeQuickPreviewAttachmentPure,
  makeQuickRefAttachment,
  makeUploadAttachment,
  mentionIdForAttachment,
  type PendingUploadAttachment,
  parseTokenFiles,
  QUICK_CASES_FOLDER,
  replaceMentionIdsInTextPrompt,
  stringifyInputValue,
} from './utils';
// 直接从子模块引入而非 barrel：避免 quick-case fetch helper 的浏览器依赖链被 utils 的纯函数测试间接拉入。
import { fetchQuickCaseFile } from './utils/fetch-quick-case-file';
import { getYoloMessageOptions, type YoloModelChoice, YoloModelDropdown } from './YoloModelDropdown';

/** 单次草稿最多允许的附件数量（含已有 + 新增），三态共用 */
const MAX_ATTACHMENTS = 20;

/** 欢迎页用户上传的临时附件前缀（区别于 quick case 展示型）。 */
const QUICK_PREVIEW_UPLOAD_PREFIX = 'quick-preview://upload/';
const isQuickPreviewPath = (filePath: string) => filePath.startsWith('quick-preview://');
/** quick case 展示型附件（未上传到 claw），开通成功后需补传转正。 */
const isUnresolvedQuickPreviewPath = (filePath: string) =>
  isQuickPreviewPath(filePath) && !filePath.startsWith(QUICK_PREVIEW_UPLOAD_PREFIX);

interface QuickPreviewResolveResult {
  ok: boolean;
  attachments: DraftAttachment[];
}

let quickPreviewAttachmentSeq = 0;

const makeQuickPreviewAttachment = (asset: NonNullable<QuickPrompt['assets']>[number]): DraftAttachment | null => {
  quickPreviewAttachmentSeq += 1;
  return makeQuickPreviewAttachmentPure(asset, quickPreviewAttachmentSeq);
};

export interface ChatInputProps {
  className?: string;
  sending?: boolean;
  onSend?: (payload: {
    content: string;
    inputValue?: TextPromptWithInputItem;
    files: ClawSendFile[];
    /** 欢迎页：session 还未建出时暂存的本地原始 File，由调用方在创建 session / 确定目录后再上传 */
    pendingFiles?: File[];
    /** 本地草稿附件，保留临时 mention id 用于真实路径替换 */
    pendingAttachments?: PendingUploadAttachment[];
    quickPromptLabel?: string;
    /** mode 类 quick prompt 选中后，需在新建 session 时透传给后端 */
    sessionMode?: SessionMode;
    /** yolo 消息级模型与清晰度选项；仅在 yolo 模式发送时传递给后端 */
    yoloOptions?: YoloMessageOptions;
    billingMode?: ClawBillingMode;
  }) => Promise<boolean> | boolean;

  /**
   * 自定义 placeholder 国际化 key，默认 'claw.chat.inputPlaceholder'。
   * Welcome 页应传入 'claw.chat.welcome.inputPlaceholder'。
   */
  placeholderKey?: string;
  /** 自定义 placeholder 文案，优先级高于 placeholderKey */
  placeholder?: string;
  /**
   * 未开通（entitled / expired）态：禁用文件上传入口（按钮置灰、文件选择不响应、不允许拖拽进来）。
   * 仅保留输入文字 + 点击 quick case 展示资源；点发送只触发开通流程。
   */
  disableUpload?: boolean;
  /** 禁用发送入口，常用于 access 尚未初始化但输入框需要先稳定展示的欢迎页。 */
  disableSend?: boolean;
  /**
   * 控制 yolo 切换按钮的开关：
   * - 已有 session：调用方按 sessionMode === 'yolo' 推一个布尔值；
   * - 欢迎页：传 undefined，由 ChatInput 内部维护本地切换状态。
   */
  yoloActive?: boolean;
  /**
   * 未开通（entitled / expired）态点发送时调用：触发开通（createClawByCode + 本地轮询），
   * resolve true 表示已开通成功（claw 进入 ACTIVE，store 已写入 activeClaw）。
   * handleSend 收到 true 后在同一次点击流程内继续真正发送。
   */
  onApplyForAccess?: () => Promise<boolean>;
  onUploadSuccess?: () => void;
  onPreviewFile?: (filePath: string) => void;
  /**
   * 输入草稿被整体清空时触发（发送成功 / 删除 skill 槽 / 极速模式清空 / 外部 resetInput 等）。
   * 欢迎页用它把 SkillSceneCard 的选中态一并取消，保证卡片高亮与输入框内容一致。
   */
  onInputReset?: () => void;
}

export interface ChatInputRestoreDraftPayload {
  /** 结构化富文本快照（含 mention 节点） */
  inputValue?: TextPromptWithInputItem;
  /** 没有结构化 inputValue 时退化用的纯文本兜底 */
  text?: string;
  /** 已上传成功的文件：以"引用型"附件回填，不重传 */
  files?: ClawSendFile[];
  /** 失败前未真正发出的 pending 上传：保留 rawFile，用户重发时再走上传 */
  pendingAttachments?: PendingUploadAttachment[];
}

export type ChatInputRef = {
  insertAssetMention: (asset: InsertAssetMentionArg) => void;
  addFiles: (files: File[]) => void;
  applyQuickPrompt: (item: QuickPrompt) => Promise<void>;
  /**
   * 把发送失败的草稿还原回输入框。已上传成功的 files 以"引用型"附件方式塞回，
   * 不重传；用户已经在写新内容时（编辑器非空 / 已有附件）保护新草稿，不覆盖。
   * @returns true 已还原；false 用户已开始写新内容，未还原。
   */
  restoreDraft: (payload: ChatInputRestoreDraftPayload) => boolean;
  /** 外部主动清空整份输入草稿（文本 / 附件 / skill 槽），并聚焦回输入框。 */
  resetInput: () => void;
};

export const BaseClawInputView = forwardRef<ChatInputRef, ChatInputProps>(
  (
    {
      sending = false,
      onSend,
      onApplyForAccess,
      placeholderKey = 'claw.chat.inputPlaceholder',
      placeholder,
      disableUpload = false,
      disableSend = false,
      yoloActive,
      onUploadSuccess,
      onPreviewFile,
      onInputReset,
      className,
    },
    ref,
  ) => {
    const t = useTranslations();
    const storeApi = useStoreApi();
    const { emitImageRejectedToast, emitImageCompressFailedToast } = useClawImageToasts(t);
    const { inputValue, inputValueRef, setInputValue, quickPromptMetaRef, resetDraftText, applyQuickPromptText } =
      useClawInputDraft();
    const [uploadingFile, setUploadingFile] = useState<boolean>(false);
    const [deletingFilePath, setDeletingFilePath] = useState('');
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const pendingUploadSlotIdRef = useRef<string | null>(null);
    // 当前经 upload 槽触发上传时，槽位限定的文件类型；通用「+上传」按钮触发时为 null（不限制）。
    const pendingUploadAcceptRef = useRef<ClawUploadAcceptKind[] | null>(null);
    const {
      attachments,
      attachmentsRef,
      setAttachments,
      appendUniqueByFilePath: appendUniqueAttachments,
      removeAttachmentsMatching,
      clearAttachments: clearAttachmentsViaHook,
    } = useClawInputAttachments();
    const clawTemplateTokenExtension = useMemo(
      () =>
        createClawTemplateTokenExtension({
          onSkillTokenRemove: () => {
            handleSkillTokenRemoveRef.current();
          },
          onUploadSlotClick: (slotId, accept) => {
            pendingUploadSlotIdRef.current = slotId;
            pendingUploadAcceptRef.current = accept ?? null;
            // 原生 accept 只是文件选择器的提示（用户仍可选「所有文件」），真校验在 uploadFiles 里补。
            if (fileInputRef.current) {
              fileInputRef.current.accept = buildUploadAcceptAttr(accept);
            }
            fileInputRef.current?.click();
          },
          onUploadSlotFileRemove: (filePath) => {
            removeAttachmentsMatching((file) => mentionIdForAttachment(file) === filePath);
          },
        }),
      [removeAttachmentsMatching],
    );
    /**
     * 未开通态点 quick case 后展示在输入框里的附件（file_path 以 quick-preview:// 开头）没有
     * 真实 claw 文件路径，无法走外层 MediaBrowser；用一个本地 modal 复用远端 url 预览，
     * 保持"点击即可看见"的体验一致。
     */
    const [localPreview, setLocalPreview] = useState<DraftAttachment | null>(null);
    const [isYoloActive, setIsYoloActive] = useState(false);
    // 欢迎页 (!activeSessionId) 阶段的本地选择；活跃 session 的选择持久在 store 里 (内存级别，按 sessionId 隔离)。
    const [welcomeYoloModelChoice, setWelcomeYoloModelChoice] = useState<YoloModelChoice>('auto');

    useEffect(() => {
      if (yoloActive === undefined) return;
      setIsYoloActive(yoloActive);
    }, [yoloActive]);
    // 让 ChatInputRef.applyQuickPrompt 可以在外部组件挂载时立即可用：handleQuickPromptSelect 在 useImperativeHandle 之后才声明，
    // 直接放进 ref deps 会触发 TDZ。这里用 ref 中转，由声明处 effect 同步最新实现。
    const handleQuickPromptSelectRef = useRef<(item: QuickPrompt) => Promise<void>>(async () => {});
    // 同理：skill chip 的 × 删除回调需要在 createClawTemplateTokenExtension（useMemo）里引用，
    // 但 resetDraftState 在更下方才声明，用 ref 中转避免 TDZ / 频繁重建扩展。
    const handleSkillTokenRemoveRef = useRef<() => void>(() => {});
    // file_name -> 已存在的 quick_cases 文件，作为索引避免重复下载&上传
    const quickCasesIndexRef = useRef<Map<string, UploadClawFileResult> | null>(null);
    // 递增 token：每次切换 quick prompt 时 ++，attachQuickPromptAssets 异步回来后
    // 对比 token 是否过期，过期则丢弃结果，避免快速切 case 时旧响应回填。
    const quickAttachTokenRef = useRef(0);
    const quickCasesIndexLoadingRef = useRef<Promise<void> | null>(null);
    const flushingBufferedDraftRef = useRef(false);
    const { uploadClawFile } = useUploadClawFile();
    const { uploadWithRenameOnConflict } = useUploadWithRenameOnConflict();
    const { access } = useClawAccess();
    const previousMentionsIds = useRef<string[]>([]);

    const {
      activeClaw,
      activeSessionId,
      bumpClawFileListRefreshKey,
      currentFolderPath,
      folderItems,
      setCurrentFolderItems,
      yoloModelChoiceBySession,
      setYoloModelChoiceForSession,
      pendingYoloModelChoiceForNextSession,
      setPendingYoloModelChoiceForNextSession,
      billingModeBySession,
      setBillingModeForSession,
      pendingBillingModeForNextSession,
      setPendingBillingModeForNextSession,
      clawPass,
      isClawInfoInitialized,
    } = useStore((state) => ({
      activeClaw: state.activeClaw,
      activeSessionId: state.activeSessionId,
      bumpClawFileListRefreshKey: state.bumpClawFileListRefreshKey,
      currentFolderPath: state.clawFolderPath,
      folderItems: state.clawCurrentFolderItems,
      setCurrentFolderItems: state.setClawCurrentFolderItems,
      yoloModelChoiceBySession: state.yoloModelChoiceBySession,
      setYoloModelChoiceForSession: state.setYoloModelChoiceForSession,
      pendingYoloModelChoiceForNextSession: state.pendingYoloModelChoiceForNextSession,
      setPendingYoloModelChoiceForNextSession: state.setPendingYoloModelChoiceForNextSession,
      billingModeBySession: state.billingModeBySession,
      setBillingModeForSession: state.setBillingModeForSession,
      pendingBillingModeForNextSession: state.pendingBillingModeForNextSession,
      setPendingBillingModeForNextSession: state.setPendingBillingModeForNextSession,
      clawPass: state.clawPass,
      isClawInfoInitialized: state.isClawInfoInitialized,
    }));
    const hasAvailableClawPassTime =
      (clawPass?.userVariant === 'clawPass' || clawPass?.userVariant === 'trial') &&
      (clawPass?.remain_seconds ?? 0) > 0;
    const defaultBillingMode: ClawBillingMode | undefined = isClawInfoInitialized
      ? hasAvailableClawPassTime
        ? 'claw_pass'
        : 'credits'
      : undefined;
    // 欢迎页 (!activeSessionId) 阶段的本地选择；活跃 session 的选择持久在 store 里（按 sessionId 隔离）。
    const [welcomeBillingMode, setWelcomeBillingMode] = useState<ClawBillingMode | undefined>(undefined);
    const billingModeTouchedRef = useRef(false);

    // 欢迎页用户未手动改动时，跟随 defaultBillingMode（有算力→claw_pass，否则 credits）。
    useEffect(() => {
      if (billingModeTouchedRef.current) return;
      setWelcomeBillingMode(defaultBillingMode);
    }, [defaultBillingMode]);

    // 当前用于 UI / 发送的扣费模式：
    // - 活跃 session：取 store 里按 sessionId 持久化的值，缺省回退到 defaultBillingMode。
    // - 欢迎页：直接用本地 welcomeBillingMode。
    const billingMode: ClawBillingMode | undefined = activeSessionId
      ? (billingModeBySession[activeSessionId] ?? defaultBillingMode)
      : welcomeBillingMode;
    const setBillingMode = (next: ClawBillingMode) => {
      if (activeSessionId) {
        setBillingModeForSession(activeSessionId, next);
      } else {
        billingModeTouchedRef.current = true;
        setWelcomeBillingMode(next);
      }
    };

    // 欢迎页发送创建新 session 后，把发送时锁定的扣费模式继承给这个新 session；
    // 普通 session 切换/页面初始化没有 pending 标记，未记录时仍回退 defaultBillingMode。
    useEffect(() => {
      if (!activeSessionId) return;
      if (pendingBillingModeForNextSession == null) return;
      const pendingBilling = pendingBillingModeForNextSession;
      // 立即清掉 pending，避免本 effect 重复消费同一份待继承值。
      setPendingBillingModeForNextSession(null);
      if (billingModeBySession[activeSessionId] !== undefined) return;
      setBillingModeForSession(activeSessionId, pendingBilling);
    }, [
      activeSessionId,
      billingModeBySession,
      setBillingModeForSession,
      pendingBillingModeForNextSession,
      setPendingBillingModeForNextSession,
    ]);

    // 当前用于 UI / 发送的 yolo 模型选择：
    // - 活跃 session：取 store 里按 sessionId 持久化的值，缺省回退到 auto。
    // - 欢迎页：直接用本地 welcomeYoloModelChoice。
    const yoloModelChoice: YoloModelChoice = activeSessionId
      ? (yoloModelChoiceBySession[activeSessionId] ?? 'auto')
      : welcomeYoloModelChoice;
    const setYoloModelChoice = (next: YoloModelChoice) => {
      if (activeSessionId) {
        setYoloModelChoiceForSession(activeSessionId, next);
      } else {
        setWelcomeYoloModelChoice(next);
      }
    };

    // 欢迎页发送创建新 yolo session 后，把发送时的欢迎页选择继承给这个新 session；
    // 普通 session 切换/页面初始化没有 pending 标记，未记录时仍默认 auto。
    useEffect(() => {
      if (!activeSessionId) return;
      if (pendingYoloModelChoiceForNextSession == null) return;
      const pendingChoice = pendingYoloModelChoiceForNextSession;
      // 立即清掉 pending，避免本 effect 重复消费同一份待继承值。
      setPendingYoloModelChoiceForNextSession(null);
      if (yoloModelChoiceBySession[activeSessionId] !== undefined) return;
      setYoloModelChoiceForSession(activeSessionId, pendingChoice);
    }, [
      activeSessionId,
      yoloModelChoiceBySession,
      setYoloModelChoiceForSession,
      pendingYoloModelChoiceForNextSession,
      setPendingYoloModelChoiceForNextSession,
    ]);
    const currentFolderItems = folderItems[currentFolderPath] || [];
    const currentFolderItemsRef = useRef(currentFolderItems);

    useEffect(() => {
      currentFolderItemsRef.current = currentFolderItems;
    }, [currentFolderItems]);

    // 当前文件夹首次未命中缓存时拉一次列表，让 @ 候选立刻有可选项；
    // 后续 listFolder 仍由文件面板等地方负责刷新，这里只兜底初始态。
    useEffect(() => {
      const clawId = activeClaw?.id;
      if (!clawId || currentFolderItemsRef.current.length > 0) return;
      let cancelled = false;
      void (async () => {
        const { data, error } = await listFolder({
          claw_id: clawId,
          folder_path: currentFolderPath || undefined,
          pagesz: 100,
        });
        if (cancelled || error || !data?.items) return;
        setCurrentFolderItems(currentFolderPath || 'media', data.items);
      })();
      return () => {
        cancelled = true;
      };
    }, [activeClaw?.id, currentFolderPath, setCurrentFolderItems]);

    const draftMentionCandidates = useMemo(
      () =>
        attachments
          .filter((file) => !!file.file_path && !!file.file_name)
          .map((file) => ({
            path: file.file_path,
            label: file.file_name ?? file.file_path,
            type: file.file_type,
            previewUrl: file.preview_url,
          })),
      [attachments],
    );

    const folderMentionCandidates = useMemo(
      () =>
        currentFolderItems
          .filter((item) => shouldDisplayMediaFile(item))
          .map((item) => {
            const path = item.path?.trim() || '';
            return {
              path,
              label: getItemName(item),
              type: getMediaItemType(item),
            };
          })
          .filter((item) => !!item.path),
      [currentFolderItems],
    );

    const { searchMentionItems, getResolvedItem } = useClawMentionSearch({
      activeClawId: activeClaw?.id,
      draftCandidates: draftMentionCandidates,
      folderCandidates: folderMentionCandidates,
    });

    const { searchSkillItems } = useClawSkillSearch({
      activeClawId: activeClaw?.id,
      // 活跃 session 只允许唤起实例上已安装的 skill；欢迎页可唤起平台技能引导安装。
      enabledGroups: ['installed'],
    });
    // `/` 唤起的技能选择器：仅在输入框为空时触发，选中后插入 skill chip。
    // 扩展只在挂载时 capture 一次回调，故用 ref 中转，保证拿到最新的 search/insert 实现。
    const searchSkillItemsRef = useRef(searchSkillItems);
    searchSkillItemsRef.current = searchSkillItems;
    // yolo 模式下禁用 `/` 唤起 skill；用 ref 中转，避免因 isYoloActive 变化重建扩展。
    const isYoloActiveRef = useRef(isYoloActive);
    isYoloActiveRef.current = isYoloActive;
    const clawSkillSuggestionExtension = useMemo(
      () =>
        createClawSkillSuggestionExtension({
          searchSkills: (query) => searchSkillItemsRef.current(query),
          onSelectSkill: (item) => {
            reportEvent('claw_element_click', { element_name: 'skill_suggestion_select' });
            // chip 已由扩展的 command 直接插入编辑器，这里仅留作埋点 / 后续元信息扩展位。
            void item;
          },
          isEnabled: () => !isYoloActiveRef.current,
        }),
      [],
    );

    const { handleClawInactiveError } = useClawInactiveHandler();

    const { mentionExtension, onUpdateCallback } = useCustomMention({
      previousMentionsIds,
      // 走 onBeforeAddMention 而不是 onMentionInsert，是为了同时覆盖：
      // 1) 用户从 popup 选中（首次插入）；
      // 2) 编辑器 undo/redo 让被删的 mention 重新出现。
      // useCustomMention 的 diff 路径也会调这里，从而把附件 chip 一起恢复。
      // 这里始终返回 true，回调只做附件的回写，不做拒绝。
      onBeforeAddMention: (item) => {
        const filePath = item.id?.trim();
        if (!filePath) return true;
        const resolved = getResolvedItem(filePath);
        setAttachments((prev) => {
          if (prev.some((f) => f.file_path === filePath)) return prev;
          return [
            ...prev,
            makeMentionAttachment({
              path: filePath,
              name: resolved?.label ?? item.label,
              type: resolved?.type === 'image' || resolved?.type === 'video' ? resolved.type : undefined,
              previewUrl: resolved?.preview,
            }),
          ];
        });
        return true;
      },
      customSearchFn: searchMentionItems,
      onBeforeDeleteMention: (deletedIds) => {
        // 用户在编辑器里删除 @<filename> 时，把对应的附件卡片也一并清掉，保持上下一致。
        // mention id 为 quick-preview:// 卡片是 `media/quick_cases/<filename>`；
        // 已上传 / @ 引用的卡片 mention id === file_path。这里用 mentionIdForAttachment 反推匹配。
        if (deletedIds.length) {
          const idSet = new Set(deletedIds);
          removeAttachmentsMatching((f) => idSet.has(mentionIdForAttachment(f)));
        }
        return true;
      },
      enableRemoteSearch: false,
      mentionColor: MENTION_COLOR,
      mentionClassName: 'text-white',
    });

    const { editor, isEmpty } = useEditorWithExtensions({
      content: inputValue,
      updateContent: setInputValue,
      extensions: [
        mentionExtension as Extension,
        clawTemplateTokenExtension as Extension,
        clawSkillSuggestionExtension as Extension,
      ],
      editorProps: {
        attributes: {
          class:
            'w-full min-h-full select-text border-0 bg-transparent px-0 py-0 text-sm leading-[22px] text-system-white outline-none focus-visible:outline-none',
        },
        handleKeyDown: (_view, event) => {
          if (event.isComposing || event.key === 'Process') {
            return true;
          }
          if (event.key !== 'Enter') return false;
          const isPhysicalEnter = event.code === 'Enter' || event.code === 'NumpadEnter';
          const isPlainEnter = !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey;
          if (!isPlainEnter) {
            event.preventDefault();
            event.stopPropagation();
            if (editor && !editor.isDestroyed) {
              editor.chain().focus().splitBlock().run();
            }
            return true;
          }

          if (!isPhysicalEnter) {
            event.preventDefault();
            event.stopPropagation();
            return true;
          }

          const mentionSuggestionState = editor ? SuggestionPluginKey.getState(editor.state) : null;
          const mentionListVisible = !!document.querySelector('.items-list');
          if (mentionSuggestionState?.active || mentionListVisible) {
            return false;
          }

          event.preventDefault();
          event.stopPropagation();
          handleSend();
          return true;
        },
      },
      onUpdateCallback,
    });

    const focusEditorEnd = () => {
      if (editor && !editor.isDestroyed) editor.commands.focus('end');
    };
    const clearEditorContent = () => {
      if (editor && !editor.isDestroyed) editor.commands.clearContent(true);
    };
    // 把附件以 @mention chip（每个文件一条，尾随空格）追加到光标末尾。
    const insertMentionChips = (chips: Array<{ id: string; label?: string }>) => {
      if (!editor || editor.isDestroyed || !chips.length) return;
      const nodes = chips.flatMap((chip) => [
        { type: 'mention', attrs: { id: chip.id, label: chip.label, color: MENTION_COLOR } },
        { type: 'text', text: ' ' },
      ]);
      editor.chain().focus('end').insertContent(nodes).run();
    };

    const resetDraftState = () => {
      clearEditorContent();
      resetDraftText();
      clearAttachmentsViaHook();
      setLocalPreview(null);
      onInputReset?.();
    };

    // 放弃整份草稿并聚焦回输入框；++token 让仍在飞的 quick prompt 资源补传作废。
    // 外部 resetInput / 删除 skill chip / 极速模式清空 skill 槽 共用此出口。
    const discardDraft = () => {
      quickAttachTokenRef.current += 1;
      resetDraftState();
      focusEditorEnd();
    };

    /** 编辑器里是否存在 skill 类型的模板槽位（用于极速模式开启时判断是否要清空）。 */
    const editorHasSkillToken = (): boolean => {
      if (!editor || editor.isDestroyed) return false;
      let found = false;
      editor.state.doc.descendants((node) => {
        if (found) return false;
        if (isSkillTemplateToken(node)) {
          found = true;
          return false;
        }
        return true;
      });
      return found;
    };

    // 删除 skill chip 不止 X 按钮一条路径：键盘退格 / 选中删除 / 剪切会直接删掉这个 atom 节点，
    // 不经过 onSkillTokenRemove。这里直接跟着受控状态 inputValue 派生：插入和删除都会刷新它，
    // 发现 skill 槽「有→无」时清掉 quick prompt 元信息并回调 onInputReset，取消 SkillSceneCard 高亮。
    // onInputReset 走 ref 读最新值，避免父级每次重渲染（内联箭头）进入依赖。
    const onInputResetRef = useRef(onInputReset);
    onInputResetRef.current = onInputReset;
    const prevHasSkillTokenRef = useRef(inputValue.some(isSkillTemplateToken));

    useEffect(() => {
      const hasSkillToken = inputValue.some(isSkillTemplateToken);
      if (prevHasSkillTokenRef.current && !hasSkillToken) {
        quickPromptMetaRef.current = null;
        onInputResetRef.current?.();
      }
      prevHasSkillTokenRef.current = hasSkillToken;
    }, [inputValue, quickPromptMetaRef]);

    /**
     * 把 editor 里所有 attrs.id === mentionId 的 mention 节点删掉。
     * 用 ProseMirror 事务一次性 delete，避免一边删一边遍历位置漂移。
     */
    const removeMentionsByIdFromEditor = (mentionId: string) => {
      if (!editor || editor.isDestroyed || !mentionId) return;
      type MentionRange = { from: number; to: number };
      const ranges: MentionRange[] = [];
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === 'mention' && node.attrs?.id === mentionId) {
          ranges.push({ from: pos, to: pos + node.nodeSize });
        }
      });
      if (!ranges.length) return;
      let tr = editor.state.tr;
      // 从后往前删，保证前面的 from/to 不被偏移影响
      for (const range of ranges.sort((a, b) => b.from - a.from)) {
        tr = tr.delete(range.from, range.to);
      }
      editor.view.dispatch(tr);
    };

    useEffect(() => {
      if (editor && !editor.isDestroyed) {
        editor.commands.focus('end');
      }
    }, [editor]);

    useEffect(() => {
      if (!editor) return;

      setTimeout(() => {
        previousMentionsIds.current = extractMentionsIdsFromEditor(editor);
      }, 0);
    }, [editor, inputValue]);

    const messageContent = stringifyInputValue(inputValue);
    const hasAttachments = attachments.length > 0;
    // 经 upload 槽位上传的文件已在编辑器里的槽位内以缩略图展示，不再重复进底部附件卡片列表。
    // 从 inputValue 收集所有 upload 槽位已挂的 filePath（buffer 态为 quick-preview://upload/...，
    // ready 态为真实 path，二者都与 attachment.file_path 精确相等），渲染底部列表时按 file_path 过滤掉。
    // 仍保留在 attachments 中：发送（sendingFiles）/ 欢迎页补传（pendingFiles）逻辑不受影响。
    const uploadSlotFilePaths = useMemo(() => {
      const paths = new Set<string>();
      for (const item of inputValue) {
        if (item.type !== 'claw-template-token' || item.clawTemplateToken.kind !== 'upload') continue;
        for (const file of item.clawTemplateToken.files ?? []) {
          if (file.filePath) paths.add(file.filePath);
        }
      }
      return paths;
    }, [inputValue]);
    const visibleAttachments = useMemo(
      () => attachments.filter((file) => !uploadSlotFilePaths.has(file.file_path)),
      [attachments, uploadSlotFilePaths],
    );
    const canSend = useClawInputCanSend({
      disableSend,
      sending,
      uploadingFile,
      flushingBufferedDraft: flushingBufferedDraftRef.current,
      messageContent,
      hasAttachments,
    });
    // 欢迎页（无 session）：只要 access 不为 null 即可上传，默认 buffer 到本地。
    // entitled / expired 态：放开上传入口，文件先 buffer 到本地草稿，不请求后端。
    // ready + 有 session：仍要求拿到 activeClaw.id 才允许上传。
    const isBufferMode = access === 'entitled' || access === 'expired';
    // 欢迎/无 session 阶段（!activeSessionId）：先把文件 buffer 到本地草稿，等发送时再统一上传。
    const shouldBufferUpload = !activeSessionId || isBufferMode;
    const canUpload = computeCanUpload({
      access,
      disableUpload,
      activeSessionId,
      activeClawId: activeClaw?.id,
      uploadingFile,
    });

    // 切换 claw 时清空所有草稿状态：输入内容、上传中的文件、quick_cases 引用、@ 引用都不应跨 claw 共享。
    // 但 entitled→ready 这一次要保留草稿（用户开通前在 welcome 输入框已经选择 quick case + 输入文本，
    // 开通成功后应该原样停留，让用户继续编辑/发送），所以只有 prevId 是真实 id 时才 reset。
    const previousClawIdRef = useRef<string | undefined>(activeClaw?.id);
    useEffect(() => {
      const prevId = previousClawIdRef.current;
      const nextId = activeClaw?.id;
      previousClawIdRef.current = nextId;
      if (prevId === nextId) return;
      // 第一次拿到 claw（undefined → real id），不重置：保留 entitled 阶段的草稿。
      if (!prevId && nextId) {
        quickCasesIndexRef.current = null;
        quickCasesIndexLoadingRef.current = null;
        return;
      }
      quickCasesIndexRef.current = null;
      quickCasesIndexLoadingRef.current = null;
      resetDraftState();
      previousMentionsIds.current = [];
    }, [activeClaw?.id, editor]);

    // 切换 session 时清空草稿：每个 session 的 ChatInput 都是独立草稿，A 写到一半切到 B 不应该带过去。
    // 未开通 welcome 自动补发正在创建首个 session 时不抢先清空；发送成功后的原逻辑会统一 reset。
    const previousSessionIdRef = useRef<string | null>(activeSessionId);
    useEffect(() => {
      const prevId = previousSessionIdRef.current;
      const nextId = activeSessionId;
      previousSessionIdRef.current = nextId;
      if (prevId === nextId) return;
      if (prevId === null && flushingBufferedDraftRef.current) return;
      resetDraftState();
      previousMentionsIds.current = [];
    }, [activeSessionId, editor]);

    // entitled → ready 切换：把展示型 quick-preview:// 附件补传到 media/quick_cases，
    // 用真实 UploadClawFileResult 替换原占位。补传期间 setUploadingFile(true) 阻止用户抢发。
    // 失败的卡片直接从 attachments 移除并 toast 提示，避免 handleSend 用一个无效 path 发出去。
    const quickPreviewBackfillPromiseRef = useRef<Promise<QuickPreviewResolveResult> | null>(null);

    /**
     * 把展示型 quick-preview:// 附件补传到 media/quick_cases，用真实 file_path 替换占位，
     * 与 prompt 文本里的 mention id 对齐。返回 ok=true 表示补传后已无「未解析」的 quick-preview 附件
     * （全部成功或本就没有）；有失败则把无效卡片清掉并 toast，返回 ok=false。
     *
     * 仅在开通成功后（claw 进入 ACTIVE）由 handleSend 显式 await 调用：保证发送前附件路径已落实，
     * 避免把 quick-preview:// 合成 path 发给后端。in-flight 时复用同一 promise，避免重复上传。
     */
    const resolveQuickPreviewAttachments = async (): Promise<QuickPreviewResolveResult> => {
      if (quickPreviewBackfillPromiseRef.current) return quickPreviewBackfillPromiseRef.current;
      const latestActiveClaw = storeApi.getState().activeClaw;
      const clawId = latestActiveClaw?.status === 'CLAW_STATUS_ACTIVE' ? latestActiveClaw.id : undefined;
      if (!clawId) return { ok: false, attachments: attachmentsRef.current };
      const targets = attachmentsRef.current.filter((f) => isUnresolvedQuickPreviewPath(f.file_path));
      if (!targets.length) return { ok: true, attachments: attachmentsRef.current };

      const run = (async (): Promise<QuickPreviewResolveResult> => {
        setUploadingFile(true);
        const failedNames: string[] = [];
        const compressFailedNames: string[] = [];
        let clawInactive = false;
        const replacements = new Map<string, DraftAttachment>();
        let nextAttachments = attachmentsRef.current;
        try {
          const index = await ensureQuickCasesIndex();
          for (const target of targets) {
            const fileName = target.file_name || extractFileNameFromUrl(target.preview_url ?? '') || 'quick-case';
            const remoteUrl = target.preview_url;
            const existed = index.get(fileName);
            if (existed) {
              replacements.set(target.file_path, makeQuickRefAttachment(existed, remoteUrl));
              continue;
            }
            if (!remoteUrl) {
              failedNames.push(fileName);
              continue;
            }
            // 转正上传前走图片校验 + 按需压缩，与 claw 公共上传层口径一致。
            const fetched = await fetchQuickCaseFile(remoteUrl, fileName, { compress: true });
            if (!fetched.ok) {
              // 压缩失败用专属 toast（imageCompressFailed），与公共上传层口径一致；
              // 不把它合并到 failedNames，否则用户分不清"网络/校验失败"和"图片压缩失败"。
              if (fetched.stage === 'compress') compressFailedNames.push(fileName);
              else failedNames.push(fileName);
              continue;
            }
            const { data, error } = await uploadClawFile({
              clawId,
              file: fetched.file,
              folderPath: QUICK_CASES_FOLDER,
            });
            if (error?.reason === 'claw_is_inactive') {
              clawInactive = true;
              break;
            }
            if (!data) {
              failedNames.push(fileName);
              continue;
            }
            const entry: UploadClawFileResult = { ...data, file_name: fileName };
            index.set(fileName, entry);
            replacements.set(target.file_path, makeQuickRefAttachment(entry, remoteUrl));
          }
        } finally {
          if (replacements.size || failedNames.length || compressFailedNames.length) {
            const shouldDropFailed = failedNames.length > 0 || compressFailedNames.length > 0;
            const currentAttachments = attachmentsRef.current;
            const removable = shouldDropFailed
              ? currentAttachments.filter(
                  (f) => isUnresolvedQuickPreviewPath(f.file_path) && !replacements.has(f.file_path),
                )
              : [];
            nextAttachments = currentAttachments.flatMap((f) => {
              if (!isUnresolvedQuickPreviewPath(f.file_path)) return [f];
              const next = replacements.get(f.file_path);
              if (next) return [next];
              return shouldDropFailed ? [] : [f];
            });
            setAttachments(nextAttachments);
            // 本次点击流程会立刻继续 build payload，手动同步 ref 避免等下一次 render。
            attachmentsRef.current = nextAttachments;
            if (replacements.size) {
              const idByFileName = new Map(
                Array.from(replacements.values()).flatMap((file) =>
                  file.file_name ? [[file.file_name, file.file_path] as const] : [],
                ),
              );
              const nextInputValue = replaceMentionIdsInTextPrompt(inputValueRef.current, idByFileName);
              setInputValue(nextInputValue);
              // handleSend 会在同一次点击流程里继续 build payload，需同步 ref 避免读到旧 mention id。
              inputValueRef.current = nextInputValue;
            }
            removable.forEach((file) => {
              removeMentionsByIdFromEditor(mentionIdForAttachment(file));
              if (file.file_name) {
                removeMentionsByIdFromEditor(file.file_name);
              }
            });
            const removedPaths = new Set(removable.map((file) => file.file_path));
            setLocalPreview((prev) => (prev && removedPaths.has(prev.file_path) ? null : prev));
          }
          if (replacements.size) {
            bumpClawFileListRefreshKey();
          }
          if (failedNames.length || compressFailedNames.length) {
            if (failedNames.length) {
              toast({
                type: 'error',
                message: `${t('claw.file.uploadFailed')}: ${failedNames.join('、')}`,
              });
            }
            emitImageCompressFailedToast(compressFailedNames);
          }
          if (clawInactive) {
            handleClawInactiveError({ reason: 'claw_is_inactive' });
          }
          setUploadingFile(false);
        }
        return {
          ok: !failedNames.length && !compressFailedNames.length && !clawInactive,
          attachments: nextAttachments,
        };
      })();

      quickPreviewBackfillPromiseRef.current = run;
      try {
        return await run;
      } finally {
        quickPreviewBackfillPromiseRef.current = null;
      }
    };

    /**
     * 批量移除草稿附件，保持卡片 / 编辑器 mention / 本地预览三者一致：
     * 1) 删掉每个附件对应的 @mention 节点；
     * 2) 从 attachments 过滤掉这些 file_path，同时 revoke 各自的 blob preview；
     * 3) 若本地预览正指向被删项则关掉，避免引用已 revoke 的 blob URL。
     *
     * 单删（用户点 ×）与 backfill 失败清理共用此出口，避免两处各写一遍。
     */
    const purgeDraftAttachments = (toRemove: DraftAttachment[]) => {
      if (!toRemove.length) return;
      const removedPaths = new Set(toRemove.map((file) => file.file_path));
      for (const file of toRemove) {
        removeMentionsByIdFromEditor(mentionIdForAttachment(file));
      }
      removeAttachmentsMatching((file) => removedPaths.has(file.file_path));
      setLocalPreview((prev) => (prev && removedPaths.has(prev.file_path) ? null : prev));
    };

    const removeAttachmentLocally = (filePath: string) => {
      const target = attachmentsRef.current.find((file) => file.file_path === filePath);
      purgeDraftAttachments(target ? [target] : []);
    };

    const handleRemoveAttachment = async (file: DraftAttachment) => {
      if (file.removable === 'local') {
        removeAttachmentLocally(file.file_path);
        return;
      }

      if (!activeClaw?.id || deletingFilePath) return;

      setDeletingFilePath(file.file_path);
      const { error } = await deleteFileOrFolder({
        claw_id: activeClaw.id,
        path: file.file_path,
        type: 'file',
      });
      setDeletingFilePath('');

      if (error) {
        toast({
          type: 'error',
          message: t('claw.file.deleteFailed'),
        });
        return;
      }

      removeAttachmentLocally(file.file_path);
      bumpClawFileListRefreshKey();
      toast({
        type: 'success',
        message: t('claw.file.deleteSuccess'),
      });
    };

    /**
     * buffer / quick-case 远程补传分支共用的图片校验：
     * - 非图片直接放行；图片走 validateImageFile（同 claw 上传层口径）
     * - 失败统一聚合成 imageRejected，调用方决定是否走 toast / 跳过
     */
    const partitionImageFiles = useCallback(async (files: File[]) => {
      const passed: File[] = [];
      const imageRejected: Array<{ name: string; reason: ImageValidationReason }> = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          passed.push(file);
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        const result = await validateImageFile(file);
        if (result.valid) {
          URL.revokeObjectURL(result.imageURL);
          passed.push(file);
        } else {
          imageRejected.push({ name: file.name, reason: result.reason });
        }
      }
      return { passed, imageRejected };
    }, []);

    /**
     * 把一批文件 buffer 到本地草稿，同时插入 @mention chip。
     * 用于 entitled/expired 态（不请求后端）和 welcome 态（等发送时再上传）。
     * 返回实际入草稿的文件列表（已做数量截断）。
     */
    const bufferFilesLocally = useCallback(
      (filesToBuffer: File[], pathPrefix: string, makeFilePath: (file: File, index: number) => string) => {
        const remaining = MAX_ATTACHMENTS - attachmentsRef.current.length;
        if (remaining <= 0) return [];
        const accepted = filesToBuffer.slice(0, remaining);
        const skipped = filesToBuffer.length - accepted.length;
        if (skipped > 0) {
          toast({ type: 'info', message: t('claw.upload.tooManyFiles', { count: MAX_ATTACHMENTS }) });
        }
        const timestamp = Date.now();
        const targetFolderPath = normalizePath(currentFolderPath) || DEFAULT_MEDIA_ROOT;
        const newAttachments = accepted.map((file, index) => {
          const fileType = inferFileTypeFromPath(file.name);
          const isVisual = fileType === 'image' || fileType === 'video';
          return {
            file_path: makeFilePath(file, index) || `${pathPrefix}${timestamp}:${index}:${file.name}`,
            file_name: file.name,
            file_type: fileType,
            preview_url: isVisual ? URL.createObjectURL(file) : undefined,
            removable: 'local' as const,
            rawFile: file,
            targetFolderPath,
          } satisfies DraftAttachment;
        });
        setAttachments((prev) => [...prev, ...newAttachments]);
        return newAttachments;
      },
      [currentFolderPath],
    );

    const appendUploadSlotFiles = useCallback(
      (slotId: string, files: ClawTemplateTokenFile[]) => {
        if (!slotId || !files.length || !editor || editor.isDestroyed) return false;

        let didUpdate = false;
        const { state, view } = editor;
        const transaction = state.tr;
        state.doc.descendants((node, pos) => {
          if (didUpdate || node.type.name !== 'claw-template-token' || node.attrs.id !== slotId) return;
          const currentFiles = parseTokenFiles(node.attrs.files);
          const existedPaths = new Set(currentFiles.map((file) => file.filePath));
          const nextFiles = [...currentFiles, ...files.filter((file) => !existedPaths.has(file.filePath))];
          transaction.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            files: JSON.stringify(nextFiles),
          });
          didUpdate = true;
        });

        if (didUpdate) {
          view.dispatch(transaction);
        }
        return didUpdate;
      },
      [editor],
    );

    const uploadFiles = useCallback(
      async (selectedFiles: File[]): Promise<void> => {
        const clawId = activeClaw?.id;
        const uploadSlotId = pendingUploadSlotIdRef.current;
        // 槽位限定类型仅在「经 upload 槽触发」时生效；通用「+上传」按钮 accept 为 null，不限制。
        const slotAccept = uploadSlotId ? pendingUploadAcceptRef.current : null;
        if (!selectedFiles.length || uploadingFile) return;

        // 校验文件类型/大小（buffer 阶段就做，与 ready 态一致）
        const whitelistedFiles = selectedFiles.filter((file) => isAllowedUploadFile(file));
        // 经 upload 槽触发且槽位声明了 accept：原生 accept 可被绕过，这里按类型再真校验一次。
        const allowedFiles = slotAccept?.length
          ? whitelistedFiles.filter((file) => isFileMatchingAccept(file, slotAccept))
          : whitelistedFiles;
        if (slotAccept?.length && allowedFiles.length < whitelistedFiles.length) {
          toast({ type: 'info', message: t('claw.upload.slotTypeRejected') });
        }
        if (!allowedFiles.length) return;
        allowedFiles.forEach((file) => {
          const folderType = file.type.startsWith('image/')
            ? 'image'
            : file.type.startsWith('video/')
              ? 'video'
              : 'file';
          reportEvent('message_upload_folder_click', { folder_type: folderType });
        });

        // 欢迎页或 entitled / expired 态：buffer 到本地草稿，用 quick-preview://upload/ 前缀区分用户上传 vs quick case。
        // ready 欢迎页也走这里，上传时机推迟到发送；外层统一落到新建 session 的 media/sess-<id>/。
        if (shouldBufferUpload) {
          // 与统一上传层口径保持一致：图片在白名单后再过一次完整校验，命中拒因的不进入草稿，
          // 失败统一聚合成 toast，避免单图弹窗。
          const { passed: bufferable, imageRejected } = await partitionImageFiles(allowedFiles);
          emitImageRejectedToast(imageRejected);
          if (!bufferable.length) return;
          const timestamp = Date.now();
          const buffered = bufferFilesLocally(
            bufferable,
            QUICK_PREVIEW_UPLOAD_PREFIX,
            (file, index) => `${QUICK_PREVIEW_UPLOAD_PREFIX}${timestamp}:${index}:${file.name}`,
          );
          const shouldFillUploadSlot =
            uploadSlotId &&
            appendUploadSlotFiles(
              uploadSlotId,
              buffered.map((attachment) => ({
                filePath: attachment.file_path,
                fileName: attachment.file_name,
                fileType: attachment.file_type,
                previewUrl: attachment.preview_url,
              })),
            );
          // 插入 @mention chip（每个文件一条，光标后追加）
          if (!shouldFillUploadSlot && buffered.length > 0) {
            insertMentionChips(buffered.map((att) => ({ id: att.file_path, label: att.file_name })));
          }
          return;
        }

        // 没有 clawId 则无法上传（ready 态兜底）
        if (!clawId) return;

        setUploadingFile(true);
        // 由 hook 处理：批量上传 + 串行解决冲突 + 统一 toast + claw 失效兜底。
        // 这里只接管 ChatInput 私有的副作用：preview_url 创建、草稿列表注入、上传成功回调。
        // onSuccess 之间 React 不会重新渲染 attachmentsRef.current，
        // 用本地计数器（mentionInsertedCount）补足"批内已加 N 个"的视图。
        let mentionInsertedCount = 0;
        const result = await uploadWithRenameOnConflict<UploadClawFileResult>({
          files: selectedFiles,
          targetFolderPath: currentFolderPath,
          isAllowedFile: isAllowedUploadFile,
          uploadOnce: async (file) => {
            const { data, error } = await uploadClawFile({
              clawId,
              file,
              folderPath: currentFolderPath,
            });
            if (data) return { ok: true, data };
            return {
              ok: false,
              clawInactive: error?.reason === 'claw_is_inactive',
              conflict: error?.reason === 'Conflict',
              moderateRejected: error?.reason === 'moderate_rejected',
            };
          },
          onSuccess: (uploadedFile, data) => {
            if (!data) return;
            const baseCount = attachmentsRef.current.length + mentionInsertedCount;
            if (baseCount >= MAX_ATTACHMENTS) return;
            mentionInsertedCount += 1;
            const attachment = makeUploadAttachment(data, uploadedFile);
            setAttachments((prev) => {
              if (prev.length >= MAX_ATTACHMENTS) return prev;
              return [...prev, attachment];
            });
            const didFillUploadSlot =
              uploadSlotId &&
              appendUploadSlotFiles(uploadSlotId, [
                {
                  filePath: attachment.file_path,
                  fileName: attachment.file_name || uploadedFile.name || attachment.file_path,
                  fileType: attachment.file_type,
                  previewUrl: attachment.preview_url,
                },
              ]);
            // 与欢迎页 buffer 上传保持一致：附件卡进草稿的同时把 @mention 插到光标处，
            // 否则 ready 态点 + 上传后输入框看不到任何引用，需用户手动 @ 一次。
            if (!didFillUploadSlot) {
              insertMentionChips([{ id: data.file_path, label: data.file_name || uploadedFile.name }]);
            }
          },
        });

        if (result.successCount > 0) {
          bumpClawFileListRefreshKey();
          onUploadSuccess?.();
        }
        setUploadingFile(false);
      },
      [
        activeClaw?.id,
        uploadingFile,
        uploadClawFile,
        uploadWithRenameOnConflict,
        currentFolderPath,
        bumpClawFileListRefreshKey,
        onUploadSuccess,
        appendUploadSlotFiles,
        shouldBufferUpload,
        bufferFilesLocally,
        partitionImageFiles,
        emitImageRejectedToast,
        editor,
      ],
    );

    const handleUploadFile = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
      const input = event.target;
      const selectedFiles = Array.from(input.files ?? []);
      try {
        await uploadFiles(selectedFiles);
      } finally {
        pendingUploadSlotIdRef.current = null;
        pendingUploadAcceptRef.current = null;
        input.value = '';
      }
    };

    const handleSend = async () => {
      if (!canSend) return;

      reportEvent('claw_massage_send_click', {
        case_name: quickPromptMetaRef.current?.label ?? '',
      });

      // 未开通（entitled / expired）态：先 await 开通（createClawByCode + 本地轮询）。
      // 成功后在同一次点击流程内继续真正发送；失败 / 超时则保留草稿原样，用户可改后重发。
      let attachmentsForPayload = attachmentsRef.current;

      if (isBufferMode) {
        // 开通 + quick case 补传期间锁住输入框，并跳过 session 切换时的草稿自动清空。
        flushingBufferedDraftRef.current = true;
        setUploadingFile(true);
        try {
          const granted = await onApplyForAccess?.();
          if (!granted) return;
          // 开通成功：把未开通态点选的展示型 quick case 附件补传到 media/quick_cases，
          // 换成真实 file_path 后再发送，避免把 quick-preview:// 合成 path 发给后端。
          const quickPreviewResult = await resolveQuickPreviewAttachments();
          if (!quickPreviewResult.ok) return;
          attachmentsForPayload = quickPreviewResult.attachments;
        } finally {
          flushingBufferedDraftRef.current = false;
          setUploadingFile(false);
        }
      }

      // 欢迎页（无 session）带 pending 暂存附件发送会创建新 session：
      // session id 由 null→real 时切换 session 的 reset effect 会清空草稿，
      // 用 flushingBufferedDraftRef 跳过那次自动清空，由本流程在发送成功后统一 resetDraftState。
      const shouldGuardWelcomeDraft = !activeSessionId && attachmentsForPayload.some(isPendingUploadAttachment);
      if (shouldGuardWelcomeDraft) {
        flushingBufferedDraftRef.current = true;
        setUploadingFile(true);
      }

      // 用最新 attachments（开通后可能刚补传过 quick case，闭包里的 attachments 已过期）构造 payload。
      // 按 file_path 去重 + 拆 pending / quick-preview，统一通过 buildClawInputPayload 出口。
      // quick-preview://... 合成 path 没有真实文件，util 内部会过滤掉。
      // pending 草稿（欢迎页暂存）走 pendingFiles 通道，由 Chat 上层在创建好 session / 确定目录后再上传。
      const payload = buildClawInputPayload({
        inputValue: inputValueRef.current,
        attachments: attachmentsForPayload,
        quickPromptMeta: quickPromptMetaRef.current,
        isYoloActive,
        yoloModelChoice,
        resolveYoloMessageOptions: getYoloMessageOptions,
      });
      if (payload.sessionMode === 'yolo' && !activeSessionId) {
        setPendingYoloModelChoiceForNextSession(yoloModelChoice);
      }
      // 欢迎页发送会创建新 session：把当前扣费模式挂到 pending，让重挂出的对话态继承，
      // 避免新实例初始化时被 defaultBillingMode（有算力→claw_pass）覆盖。
      if (!activeSessionId) {
        setPendingBillingModeForNextSession(billingMode ?? null);
      }
      try {
        const sent = await onSend?.({ ...payload, billingMode });
        if (!sent) {
          setPendingYoloModelChoiceForNextSession(null);
          setPendingBillingModeForNextSession(null);
          return;
        }

        resetDraftState();
        focusEditorEnd();
      } finally {
        if (shouldGuardWelcomeDraft) {
          flushingBufferedDraftRef.current = false;
          setUploadingFile(false);
        }
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        insertAssetMention: (asset) => {
          if (!editor || editor.isDestroyed) return;
          const filePath = asset.path?.trim();
          if (!filePath) return;
          setInputValue((prev) => [
            ...prev,
            {
              type: 'mention',
              mention: { id: filePath, name: asset.name, color: MENTION_COLOR },
            },
            { type: 'text', text: ' ' },
          ]);
          requestAnimationFrame(focusEditorEnd);

          // 同一个文件多次 @ 时 mention 可叠加，但草稿卡片按 file_path 唯一
          setAttachments((prev) =>
            prev.some((f) => f.file_path === filePath) ? prev : [...prev, makeMentionAttachment(asset)],
          );
        },
        addFiles: (files: File[]) => {
          pendingUploadSlotIdRef.current = null;
          pendingUploadAcceptRef.current = null;
          uploadFiles(files);
        },
        applyQuickPrompt: (item) => handleQuickPromptSelectRef.current(item),
        restoreDraft: ({ inputValue: snapshotInputValue, text, files, pendingAttachments }) => {
          if (!editor || editor.isDestroyed) return false;
          // 用户在等待期间已经在写新内容：保护新草稿，不覆盖。
          const editorIsDirty = !editor.isEmpty || attachmentsRef.current.length > 0;
          if (editorIsDirty) return false;

          const refAttachments: DraftAttachment[] = (files ?? []).map((f) =>
            makeMentionAttachment({
              path: f.file_path,
              name: f.file_path.split('/').pop() || f.file_path,
              type: f.file_type,
            }),
          );
          const pendingRefs: DraftAttachment[] = (pendingAttachments ?? []).map(
            ({ file, tempFilePath, targetFolderPath }) =>
              makeQuickPendingAttachment({
                file,
                fileName: file.name,
                fileType: inferFileTypeFromPath(file.name),
                tempPath: tempFilePath,
                targetFolderPath,
              }),
          );
          setAttachments([...refAttachments, ...pendingRefs]);

          if (snapshotInputValue?.length) {
            setInputValue(snapshotInputValue);
          } else if (text) {
            setInputValue([{ type: 'text', text }]);
          }

          requestAnimationFrame(focusEditorEnd);
          return true;
        },
        resetInput: () => {
          discardDraft();
        },
      }),
      [editor, uploadFiles],
    );

    // 懒加载 media/quick_cases 目录索引，作为复用判断的 source of truth
    const ensureQuickCasesIndex = async (): Promise<Map<string, UploadClawFileResult>> => {
      if (quickCasesIndexRef.current) return quickCasesIndexRef.current;
      const clawId = activeClaw?.id;
      if (!clawId) return new Map();

      if (!quickCasesIndexLoadingRef.current) {
        quickCasesIndexLoadingRef.current = (async () => {
          const { data, error } = await listFolder({
            claw_id: clawId,
            folder_path: QUICK_CASES_FOLDER,
            pagesz: 100,
          });
          const map = new Map<string, UploadClawFileResult>();
          // 目录不存在或拉取失败：使用空索引，后续上传会自动创建目录
          if (!error && data?.items) {
            data.items.forEach((it) => {
              if (isFolder(it)) return;
              const name = getItemName(it);
              const filePath = it.path?.trim();
              if (!name || !filePath) return;
              map.set(name, {
                file_path: filePath,
                file_type: getMediaItemType(it),
                file_name: name,
              });
            });
          }
          quickCasesIndexRef.current = map;
        })().finally(() => {
          quickCasesIndexLoadingRef.current = null;
        });
      }
      await quickCasesIndexLoadingRef.current;
      return quickCasesIndexRef.current ?? new Map();
    };

    /**
     * quick-case 拉取/上传分支共用的 uploadingFile 守卫：进入时置 true，
     * 结束时仅当 token 仍为最新（用户未切换 quick prompt）才复位，避免旧请求把新选择的 loading 态清掉。
     */
    const withUploadingGuard = async (token: number, fn: () => Promise<void>) => {
      setUploadingFile(true);
      try {
        await fn();
      } finally {
        if (quickAttachTokenRef.current === token) {
          setUploadingFile(false);
        }
      }
    };

    const attachQuickPromptAssets = async (item: QuickPrompt, token: number) => {
      const clawId = activeClaw?.id;
      if (!item.assets?.length) return;

      if (!activeSessionId) {
        await withUploadingGuard(token, async () => {
          const refs: DraftAttachment[] = [];
          const quickRejected: Array<{ name: string; reason: ImageValidationReason }> = [];
          for (const asset of item.assets ?? []) {
            const url = asset.url;
            if (!url) continue;
            const fileName = extractFileNameFromUrl(url) || 'quick-case';
            const fileType: ClawDraftFileType =
              asset.type === 'image' || asset.type === 'video' || asset.type === 'audio' ? asset.type : 'file';
            // welcome 暂存：只 buffer 原始 File，发送时再上传，故不压缩。
            const fetched = await fetchQuickCaseFile(url, fileName, { compress: false });
            if (!fetched.ok) {
              // 远程拉到的 quick-case 素材也走同一层图片校验，不合格的不入 quick-case 索引；
              // fetch 失败（网络/404）静默跳过，与原逻辑一致。
              if (fetched.stage === 'validate') quickRejected.push({ name: fileName, reason: fetched.reason });
              continue;
            }
            quickPreviewAttachmentSeq += 1;
            refs.push(
              makeQuickPendingAttachment({
                file: fetched.file,
                fileName,
                fileType,
                previewUrl: fileType === 'image' || fileType === 'video' ? url : undefined,
                tempPath: `quick-preview://upload/quick/${quickPreviewAttachmentSeq}/${fileName}`,
              }),
            );
          }
          emitImageRejectedToast(quickRejected);

          if (quickAttachTokenRef.current !== token) return;
          appendUniqueAttachments(refs);
          const idByFileName = new Map(
            refs.flatMap((ref) => (ref.file_name ? [[ref.file_name, ref.file_path] as const] : [])),
          );
          setInputValue((prev) => replaceMentionIdsInTextPrompt(prev, idByFileName));
        });
        return;
      }

      // 未开通（entitled / expired）态：禁用真实上传，只做"展示型"附件卡片：
      // file_path 形如 quick-preview://<seq>/<filename>，preview_url 直接复用远端 url，
      // 用户能在卡片里看到预览也能点击放大；handleSend 会跳过这类合成 path 不进 sendingFiles。
      // 开通成功后由 handleSend 显式把这些卡片补传到 media/quick_cases，
      // 替换成真实 file_path，跟 prompt 文本里的 mention id 对齐。
      if (isBufferMode || !clawId) {
        const refs = item.assets
          .map((asset) => makeQuickPreviewAttachment(asset))
          .filter((draft): draft is DraftAttachment => draft !== null);
        if (quickAttachTokenRef.current !== token) return;
        appendUniqueAttachments(refs);
        return;
      }

      await withUploadingGuard(token, async () => {
        const index = await ensureQuickCasesIndex();
        const refs: DraftAttachment[] = [];
        const quickRejected: Array<{ name: string; reason: ImageValidationReason }> = [];
        const compressFailedNames: string[] = [];

        for (const asset of item.assets ?? []) {
          const url = asset.url;
          if (!url) continue;
          const fileName = extractFileNameFromUrl(url) || 'quick-case';
          const existed = index.get(fileName);
          if (existed) {
            refs.push(makeQuickRefAttachment(existed, asset.type === 'image' ? url : undefined));
            continue;
          }

          // 走与上传层一致的图片校验 + 按需压缩：不合格的不进 media/quick_cases，也不入 quick-case 索引。
          const fetched = await fetchQuickCaseFile(url, fileName, { compress: true });
          if (!fetched.ok) {
            // fetch 失败（网络/404）静默跳过；校验失败累计到 quickRejected；
            // 压缩失败累计到 compressFailedNames，最后通过专属 toast 让用户感知。
            if (fetched.stage === 'validate') quickRejected.push({ name: fileName, reason: fetched.reason });
            else if (fetched.stage === 'compress') compressFailedNames.push(fileName);
            continue;
          }
          const { data } = await uploadClawFile({
            clawId,
            file: fetched.file,
            folderPath: QUICK_CASES_FOLDER,
          });
          if (!data) continue;

          const entry: UploadClawFileResult = { ...data, file_name: fileName };
          index.set(fileName, entry);
          refs.push(makeQuickRefAttachment(entry, asset.type === 'image' ? url : undefined));
        }
        emitImageRejectedToast(quickRejected);
        emitImageCompressFailedToast(compressFailedNames);

        if (quickAttachTokenRef.current !== token) return;

        if (refs.length) {
          appendUniqueAttachments(refs);
          bumpClawFileListRefreshKey();
        }
      });
    };

    const handleQuickPromptSelect = async (item: QuickPrompt) => {
      quickPromptMetaRef.current = { label: item.label ?? '', mode: item.mode };
      // quick prompt 自带 mode 时同步切到 yolo 切换按钮，保证 UI 与最终 createSession 的 mode 一致：
      // - mode === 'yolo' → 打开 yolo 切换
      // - 其他 mode → 关闭 yolo 切换（quick prompt 的 mode 优先生效）
      // - 没 mode → 保持用户当前选择不变
      if (item.mode === 'yolo') {
        setIsYoloActive(true);
        // quick prompt 强制把当前上下文的选择回到 auto。活跃 session 写回 store；欢迎页只动本地。
        setYoloModelChoice('auto');
      } else if (item.mode) {
        setIsYoloActive(false);
      }
      // 切换 quick prompt 视为替换整份输入草稿：清空全部附件，避免附件卡片和新 prompt 里的 @ 不一致。
      // 同时 ++token 让仍在飞的旧请求作废。
      const token = ++quickAttachTokenRef.current;
      clearAttachmentsViaHook();
      setLocalPreview(null);

      applyQuickPromptText(item, (fileName) => fileName);
      // 等编辑器同步内容后再聚焦到末尾，避免插入与 focus 竞争
      requestAnimationFrame(focusEditorEnd);

      await attachQuickPromptAssets(item, token);
    };

    handleQuickPromptSelectRef.current = handleQuickPromptSelect;

    // 删除 skill chip 视为放弃整份技能模板：清空全部输入草稿（文本 / 附件 / 其它槽位），
    // 并聚焦回输入框。resetDraftState → resetDraftText 已负责把 quickPromptMetaRef 置空。
    handleSkillTokenRemoveRef.current = discardDraft;

    const attachButton = (
      <Button
        variant="text"
        size="sm"
        disabled={!canUpload}
        loading={uploadingFile}
        loadingClassName="mr-0 animate-spin text-system-white"
        className="flex size-8 shrink-0 items-center justify-center rounded-10 p-1 text-system-white40 transition-colors hover:bg-system-white08 light:hover:bg-[rgba(18,24,38,0.04)] hover:text-system-white light:hover:text-[#121826]"
        onClick={() => {
          pendingUploadSlotIdRef.current = null;
          pendingUploadAcceptRef.current = null;
          // 通用「+上传」不限制类型：复位共享 input 的 accept，避免沿用上一次槽位的窄类型。
          if (fileInputRef.current) {
            fileInputRef.current.accept = CLAW_UPLOAD_ACCEPT;
          }
          fileInputRef.current?.click();
        }}
      >
        {!uploadingFile && <Plus className="size-5" strokeWidth={1.75} />}
      </Button>
    );

    const yoloToggle = (
      <button
        type="button"
        aria-pressed={isYoloActive}
        onClick={() => {
          reportEvent('claw_element_click', { element_name: 'yolo_mode_toggle' });
          const nextActive = !isYoloActive;
          // 极速模式不支持技能模板：开启极速模式时若输入框里还挂着 skill 槽位，
          // 整份输入草稿（含 skill / 附件）一并清空，避免把技能模板带进极速发送。
          if (nextActive && editorHasSkillToken()) {
            discardDraft();
          }
          setIsYoloActive(nextActive);
          // 用户手动切 yolo 之后，清掉 quick prompt 已绑定的 mode，
          // 否则 handleSend 里 `quickPromptMeta?.mode ?? ...` 会让 quick mode 继续覆盖 yolo 状态。
          if (quickPromptMetaRef.current?.mode) {
            quickPromptMetaRef.current = { ...quickPromptMetaRef.current, mode: undefined };
          }
        }}
        className={cn(
          'flex h-8 shrink-0 items-center gap-1 rounded-12 px-3 text-xs transition-colors',
          isYoloActive
            ? 'bg-claw-brand text-white hover:bg-claw-brand/90'
            : 'light:text-[rgba(18,24,38,0.6)] text-system-white40 hover:bg-system-white08 light:hover:bg-[rgba(18,24,38,0.04)] hover:text-system-white light:hover:text-[#121826]',
        )}
      >
        <Zap className="size-4" />
        <span>{t('claw.chat.yoloModeLabel')}</span>
      </button>
    );

    const yoloOptionsControls = <YoloModelDropdown value={yoloModelChoice} onChange={setYoloModelChoice} />;

    const editorArea = (
      <EditorArea
        editor={editor}
        isEmpty={isEmpty}
        placeholderText={isYoloActive ? t('claw.chat.yoloInputPlaceholder') : placeholder || t(placeholderKey)}
      />
    );

    const sendButton = (
      <Button
        variant="outline"
        size="sm"
        disabled={!canSend}
        className="flex h-8 w-12 shrink-0 items-center justify-center rounded-12 p-0 text-system-white40 transition-colors hover:bg-system-white08 light:hover:bg-[#5765F2]/90 hover:text-system-white light:enabled:bg-[#5765F2] light:enabled:text-white disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() => {
          handleSend();
        }}
      >
        <BaseLineSubmit3 className="size-4" />
      </Button>
    );

    const billingModeControls = (
      <BillingModeControls
        billingMode={billingMode}
        onBillingModeChange={(nextBillingMode) => {
          setBillingMode(nextBillingMode);
        }}
      />
    );

    const hiddenFileInput = (
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={CLAW_UPLOAD_ACCEPT}
        className="hidden"
        onChange={(event) => {
          handleUploadFile(event);
        }}
      />
    );

    return (
      <>
        <div
          className={cn(
            'relative flex w-full flex-col items-end gap-2 rounded-16 border border-system-white08 bg-system-white04 light:bg-[#F4F4FA] px-4 py-3 transition-colors',
            className,
          )}
        >
          {visibleAttachments.length > 0 && (
            <div className="mb-2 flex w-full min-w-0 gap-3 overflow-x-auto overflow-y-hidden pb-1">
              {visibleAttachments.map((file) => (
                <DraftAttachmentCard
                  key={file.file_path}
                  file={file}
                  removing={file.removable === 'remote' && deletingFilePath === file.file_path}
                  onClick={() => {
                    // quick-preview:// 是 entitled 态展示型附件（未上传到 claw），没有真实 path，
                    // 走 MediaBrowser 找不到；这里直接用本地 modal 复用远端 url 预览。
                    if (isQuickPreviewPath(file.file_path)) {
                      setLocalPreview(file);
                      return;
                    }
                    onPreviewFile?.(file.file_path);
                  }}
                  onRemove={() => handleRemoveAttachment(file)}
                />
              ))}
            </div>
          )}

          {hiddenFileInput}

          <div className="flex w-full flex-col gap-2">
            {editorArea}
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex items-center justify-start gap-2">
                {attachButton}
                {!activeSessionId || isYoloActive ? (
                  <div className="mx-1 h-5 w-px bg-system-white12 light:bg-[rgba(18,24,38,0.08)]" aria-hidden />
                ) : null}
                {!activeSessionId && yoloToggle}
                {isYoloActive ? yoloOptionsControls : null}
              </div>
              <div className="flex items-center justify-end gap-2">
                {billingModeControls}
                {sendButton}
              </div>
            </div>
          </div>
        </div>
        <DraftLocalPreviewDialog file={localPreview} onClose={() => setLocalPreview(null)} />
      </>
    );
  },
);

BaseClawInputView.displayName = 'BaseClawInputView';

export { ChatInputDisclaimer } from './ChatInputDisclaimer';
